import { useEffect, useState, useRef } from 'react';
import socketService from '../services/socketService';

/**
 * Socket 连接管理 hook
 * 负责：连接建立、认证、事件监听注册/清理
 */
export function useSocket(isAuthenticated, token, onMessageSync, onConversationSync, onTitleUpdated) {
  const [socketConnected, setSocketConnected] = useState(false);
  const callbackRefs = useRef({ onMessageSync, onConversationSync, onTitleUpdated });

  // 保持回调引用最新
  useEffect(() => {
    callbackRefs.current = { onMessageSync, onConversationSync, onTitleUpdated };
  });

  useEffect(() => {
    if (!isAuthenticated || !token) return;

    const socketUrl = process.env.REACT_APP_SOCKET_URL;

    // 1. 先注册连接/断开回调（使用内部数组，不依赖socket实例）
    const onConnectCallback = () => {
      setSocketConnected(true);
      socketService.authenticate(token);
    };
    const onDisconnectCallback = (reason) => {
      console.log('[Socket] 连接断开:', reason);
      setSocketConnected(false);
    };
    socketService.onConnect(onConnectCallback);
    socketService.onDisconnect(onDisconnectCallback);

    // 2. 建立连接（同步创建socket实例，连接本身是异步的）
    socketService.connect(socketUrl);

    // 3. socket已创建，注册事件监听器
    const onAuthenticated = (data) => {
      if (data.success) {
        console.log('[Socket] 认证成功');
      } else {
        console.error('[Socket] 认证失败:', data.error);
      }
    };
    const onMessageSyncHandler = (data) => {
      callbackRefs.current.onMessageSync?.(data);
    };
    const onConversationSyncHandler = (data) => {
      callbackRefs.current.onConversationSync?.(data);
    };
    const onTitleUpdatedHandler = (data) => {
      console.log('[Socket] 对话标题已更新:', data);
      callbackRefs.current.onTitleUpdated?.(data);
    };
    const onMessageError = (error) => console.error('[Socket] 消息错误:', error);
    const onConversationError = (error) => console.error('[Socket] 对话错误:', error);

    socketService.on('authenticated', onAuthenticated);
    socketService.on('message_sync', onMessageSyncHandler);
    socketService.on('conversation_sync', onConversationSyncHandler);
    socketService.on('conversation_title_updated', onTitleUpdatedHandler);
    socketService.on('messageError', onMessageError);
    socketService.on('conversationError', onConversationError);

    return () => {
      socketService.off('authenticated', onAuthenticated);
      socketService.off('message_sync', onMessageSyncHandler);
      socketService.off('conversation_sync', onConversationSyncHandler);
      socketService.off('conversation_title_updated', onTitleUpdatedHandler);
      socketService.off('messageError', onMessageError);
      socketService.off('conversationError', onConversationError);
      socketService.disconnect();
    };
  }, [isAuthenticated, token]);

  return { socketConnected };
}
